import { css, html, LitElement, nothing, type PropertyValues } from "lit";

import { actionDescriptors, planArtifactAction } from "../lib/artifact-actions";
import { browserStorage, TakenBaseMemory } from "../lib/base-memory";
import {
  baseVersionToDeclare,
  lockErrorText,
  lockStateOf,
  planUploadLock,
  takeoverConfirmText,
  type LockState,
} from "../lib/locking";
import { planUpload } from "../lib/upload-plan";
import { controlStyles } from "../styles/tokens";
import {
  TransportError,
  type ArtifactActionId,
  type ArtifactActionIntent,
  type ArtifactDetail,
  type ArtifactListRow,
  type ArtifactSearchRow,
  type ClientRow,
  type ProjectRow,
  type StorageTransport,
} from "../transport/types";
import "./artifact-table";
import "./labeled-select";
import "./lock-panel";
import "./search-bar";
import "./status-area";
import "./upload-panel";
import "./versions-panel";
import type { LockActionEvent } from "./artifact-table";
import type { LockPanelMode } from "./lock-panel";
import type {
  ModulinaUploadPanel,
  UploadAppendInfo,
  UploadProgress,
} from "./upload-panel";

/**
 * <modulina-artifact-browser> — the shared W2 screen (spec-web-ui §2) AND
 * the SketchUp HtmlDialog body (ui-surface.md "app forma = kanonas"):
 * client → project selects, explicit search, sortable table, versions
 * panel, per-artifact ACTIONS. Composed of pure children; the ONLY thing
 * it knows about the outside world is the injected task-shaped
 * {@link StorageTransport} — no HTTP, no hosts, no credentials
 * (falsification point §7.2 guards this boundary).
 *
 * EVERY shell difference is capability data, never host branching:
 * caps.actions decides which row/version buttons exist (browser:
 * download; SketchUp: open/import), and open/import route to the
 * transport's performArtifactAction (the browser fulfils download itself).
 * initialClientId/initialProjectId let a shell preselect a context
 * (SketchUp's active-model assignment); the browser passes neither.
 *
 * Events up to the shell: `artifact-opened` / `artifact-closed` (history
 * deep-link sync) and `session-expired` (any 401 → shell drops to login).
 */

interface Status {
  kind: "" | "error" | "ok" | "info" | "loading";
  code?: string;
  text?: string;
}

/**
 * The pending upload DESCRIPTOR (spec-upload-unification §3): what the
 * confirm/submit flow actually needs — a name to plan create-vs-append
 * with and a size to show. `file` exists only on the browser's "file"
 * source path (the REST adapter requires the bytes); the SketchUp
 * "activeModel" path has none — its bytes are Ruby-mediated, uploadFile
 * is an intent.
 */
export interface PendingUpload {
  name: string;
  size: number | null;
  file?: File;
  /**
   * SNAPSHOT of the return target this pick was planned for (present only on
   * a „Grąžinti" flow — spec-artifact-locking §8). Submit reads THIS, never
   * the live `returnTarget`: a confirm belongs to exactly one upload, and the
   * live field can change under an open confirm (a „Grąžinti" click while a
   * plain-upload confirm is open, an „Atsisakyti" while a return confirm is).
   * Reading the live field there sent one pick's bytes to another pick's
   * artifact — the 127d wrong-target class, one layer up.
   */
  returnTo?: { artifactId: string; name: string };
}

/** The dirty-model pending note (spec-upload-unification §3 — component-
 * worded, NOT an error-registry entry: being dirty is a normal state the
 * host handles by saving first, the user just must not be surprised). */
const DIRTY_MODEL_NOTE = "Modelis turi neišsaugotų pakeitimų — prieš įkeliant bus išsaugotas.";

/**
 * The pending APPEND target (spec-artifact-locking §8): the confirm info the
 * upload panel shows, plus the two facts the take-for-work rules need at
 * SEND time — the file's lock state (does this upload have to claim the lock
 * first?) and its current version (the base to declare when the user never
 * "took" it). Both are read off the SAME fresh list the confirm was planned
 * from, so the decision and what the user was shown cannot disagree.
 */
interface PendingAppend extends UploadAppendInfo {
  artifactId: string;
  currentVersionNumber: number;
  lockState: LockState;
}

/** The open take/takeover confirm (rendered by <modulina-lock-panel>). */
interface LockPanelState {
  mode: LockPanelMode;
  artifactId: string;
  fileName: string;
  /** Takeover only: the „kieno ir kada" sentence, composed with the clock. */
  confirmText: string;
}

export class ModulinaArtifactBrowser extends LitElement {
  static override properties = {
    transport: { attribute: false },
    deepLinkArtifactId: { attribute: false },
    initialClientId: { attribute: false },
    initialProjectId: { attribute: false },
    me: { attribute: false },
    clients: { state: true },
    projects: { state: true },
    clientId: { state: true },
    projectId: { state: true },
    rows: { state: true },
    rowsMode: { state: true },
    rowsLoaded: { state: true },
    searchAll: { state: true },
    openArtifact: { state: true },
    panelLoading: { state: true },
    status: { state: true },
    pendingUpload: { state: true },
    uploadPendingNote: { state: true },
    uploadAppendInfo: { state: true },
    uploading: { state: true },
    uploadProgress: { state: true },
    highlightId: { state: true },
    actionRun: { state: true },
    actionProgress: { state: true },
    lockPanel: { state: true },
    lockBusy: { state: true },
    returnTarget: { state: true },
  };

  declare transport: StorageTransport | null;
  /** Set by the shell when the URL is /artifacts/:id (R23 deep link). */
  declare deepLinkArtifactId: string | null;
  /** ONE-SHOT preselect pushed by the shell (SketchUp: the active model's
   * client/project assignment). Applied after the client list loads, then
   * consumed — the user's later navigation always wins. The browser shell
   * passes neither (they stay null → no-op → browser byte-identical). */
  declare initialClientId: string | null;
  declare initialProjectId: string | null;
  /** WHO the signed-in user is, as the server records lock holders: the
   * audit principal, which for a web session is the user's EMAIL
   * (src/http/auth.ts setWebSessionActor). Needed to tell "taken by me"
   * from "taken by someone" (spec-artifact-locking §8) and to namespace the
   * take-for-work memory. null = identity unknown → every lock reads as a
   * colleague's, and the memory falls back to this tab only. */
  declare me: string | null;

  declare clients: ClientRow[];
  declare projects: ProjectRow[];
  declare clientId: string;
  declare projectId: string;
  declare rows: ArtifactListRow[] | ArtifactSearchRow[];
  declare rowsMode: "project" | "search";
  declare rowsLoaded: boolean;
  declare searchAll: boolean;
  declare openArtifact: ArtifactDetail | null;
  declare panelLoading: boolean;
  declare status: Status;

  // ----- W3 upload flow state (spec-web-ui §2 "Upload") -----
  /** The pending pick awaiting confirm/send (null = upload panel idle).
   * A descriptor, not a File: the "activeModel" source has no File —
   * see {@link PendingUpload}. */
  declare pendingUpload: PendingUpload | null;
  /** Muted note under the pending line (the dirty-model note on the
   * "activeModel" path; null = none). */
  declare uploadPendingNote: string | null;
  /** Set when the file name matches exactly ONE existing artifact —
   * drives the pre-send "Bus sukurta nauja versija …" confirm. Carries the
   * target id (so submit appends without re-deciding) plus the lock/base
   * facts the take-for-work rules need — see {@link PendingAppend}. */
  declare uploadAppendInfo: PendingAppend | null;
  declare uploading: boolean;
  declare uploadProgress: UploadProgress | null;
  /** Row to highlight after a successful upload (table selectedId when no
   * versions panel is open). */
  declare highlightId: string | null;

  // ----- capability-driven action state (open/import via the shell) -----
  /** The in-flight non-download action (label for the progress/cancel
   * row); null = idle. Only set on transports with performArtifactAction
   * (SketchUp) — the browser's "download" path never touches it, so the
   * download UX stays byte-identical. */
  declare actionRun: { label: string } | null;
  /** Byte progress of the in-flight action (null total = indeterminate). */
  declare actionProgress: { bytes: number; totalBytes: number | null } | null;

  // ----- take-for-work state (spec-artifact-locking §8) -----
  /** The open „Paimti darbui" / „Perimti…" confirm; null = none. */
  declare lockPanel: LockPanelState | null;
  /** A lock/unlock call is in flight — one at a time, buttons disabled. */
  declare lockBusy: boolean;
  /** The file a „Grąžinti" click is waiting for: the next picked file must
   * BE this one, or the user silently created a second artifact instead of
   * returning their work. null = an ordinary upload. */
  declare returnTarget: { artifactId: string; name: string } | null;

  /** Abort seam for the in-flight upload — not reactive (no render effect). */
  private uploadController: AbortController | null = null;
  /** The query the visible rows were loaded with, so a refresh after a lock
   * change re-renders the SAME view (a lock badge must not silently widen
   * or narrow what the user was looking at). */
  private lastQuery = "";
  /** Base-version memory for the CURRENT user, rebuilt when `me` changes —
   * a base one employee remembered must never be declared by another. Not
   * reactive: it is storage, not render state. */
  private baseMemoryInstance: TakenBaseMemory | null = null;
  private baseMemoryFor: string | null = null;
  /** In-flight guard for onModelPick: while getModelInfo→beginPendingUpload
   * is awaited, pendingUpload is still null and the idle model button stays
   * rendered — repeat clicks must not launch concurrent flows (duplicate
   * listArtifacts, interleaved state writes). Not reactive: the button
   * simply no-ops until the pick settles. */
  private modelPickInFlight = false;
  /** Abort seam for the in-flight action fulfilment (open/import). */
  private actionController: AbortController | null = null;
  /** initialClientId/initialProjectId are one-shot — consumed here. */
  private initialContextApplied = false;

  /** Keyboard continuity (review minor 5): the element that triggered the
   * versions panel (usually a table row) — focus returns to it when the
   * panel closes (Esc AND the close button funnel through closePanel), so
   * a keyboard user never drops to <body>. Not reactive: focus is a DOM
   * side effect, not render state. */
  private restoreFocusTo: HTMLElement | null = null;

  constructor() {
    super();
    this.transport = null;
    this.deepLinkArtifactId = null;
    this.initialClientId = null;
    this.initialProjectId = null;
    this.me = null;
    this.clients = [];
    this.projects = [];
    this.clientId = "";
    this.projectId = "";
    this.rows = [];
    this.rowsMode = "project";
    this.rowsLoaded = false;
    this.searchAll = false;
    this.openArtifact = null;
    this.panelLoading = false;
    this.status = { kind: "" };
    this.pendingUpload = null;
    this.uploadPendingNote = null;
    this.uploadAppendInfo = null;
    this.uploading = false;
    this.uploadProgress = null;
    this.highlightId = null;
    this.actionRun = null;
    this.actionProgress = null;
    this.lockPanel = null;
    this.lockBusy = false;
    this.returnTarget = null;
  }

  /** The take-for-work memory for the CURRENT user (spec §8: the shell
   * remembers the base version so the later return can declare it). */
  private get baseMemory(): TakenBaseMemory {
    if (this.baseMemoryInstance === null || this.baseMemoryFor !== this.me) {
      this.baseMemoryInstance = new TakenBaseMemory(browserStorage(globalThis), this.me);
      this.baseMemoryFor = this.me;
    }
    return this.baseMemoryInstance;
  }

  /** Upload affordance: capability AND a single upload target (a tenant-wide
   * search view has none). Also the gate for „Grąžinti", which IS an upload. */
  private get canUpload(): boolean {
    return (
      this.transport?.caps.upload === true &&
      this.projectId !== "" &&
      this.rowsMode === "project"
    );
  }

  /**
   * Does this shell offer take-for-work at all? PRESENCE of the lock methods
   * is the capability signal (StorageTransport docblock), combined with the
   * write affordance: locking is a write, so a read-scope user (caps.upload
   * false) is never shown buttons the server would refuse. A transport
   * without the methods — today's SketchUp adapter — renders badges only.
   */
  private get lockUi(): boolean {
    const transport = this.transport;
    return (
      transport?.lockArtifact !== undefined &&
      transport.unlockArtifact !== undefined &&
      transport.caps.upload
    );
  }

  /**
   * Does this shell hand bytes to the user ITSELF? Pure capability data
   * (`caps.actions`, the same source the row buttons render from): the
   * browser advertises "download" and „Paimti darbui" mints a link; the
   * SketchUp dialog advertises open/import and has NO browser to navigate
   * (ui/BRIDGE.md §4.3 — "no download action here", channel reply 127
   * item 3), so a take there is the claim alone.
   */
  private get canDownload(): boolean {
    return this.transport?.caps.actions.includes("download") === true;
  }

  /** How the user gets the file on a shell that does not download: name its
   * PRIMARY advertised action, so the sentence stays capability data instead
   * of a hardcoded „Atidaryti". */
  private loadHint(): string {
    const primary = this.transport?.caps.actions[0];
    return primary ? `naudokite mygtuką „${this.actionLabel(primary)}"` : "failas rezervuotas jums";
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        gap: var(--m-space-3);
      }
      .nav-row {
        display: flex;
        gap: var(--m-space-3);
      }
      .nav-row modulina-select {
        flex: 1;
      }
      .hint {
        color: var(--m-color-text-muted);
        font-size: var(--m-fs-small);
      }
      /* Progress + cancel row for a long-running action fulfilment
         (open/import) — rendered only while an action is in flight, which
         only happens on shells with performArtifactAction (SketchUp). */
      .action-run {
        display: flex;
        align-items: center;
        gap: var(--m-space-3);
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        background: var(--m-color-surface);
        box-shadow: var(--m-shadow);
        padding: var(--m-space-2) var(--m-space-3);
        font-size: var(--m-fs-small);
      }
      .action-run .spacer {
        flex: 1;
      }
    `,
  ];

  protected override firstUpdated(): void {
    void this.loadClients();
    if (this.deepLinkArtifactId) void this.openById(this.deepLinkArtifactId);
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    // Shell navigation (back/forward) can change the deep link at any time.
    if (changed.has("deepLinkArtifactId") && changed.get("deepLinkArtifactId") !== undefined) {
      if (this.deepLinkArtifactId) {
        void this.openById(this.deepLinkArtifactId);
      } else {
        this.openArtifact = null;
      }
    }
  }

  override render() {
    if (!this.transport) return nothing;
    // Upload affordance ONLY when the capability exists AND a project is
    // selected AND the table is not showing tenant-wide search hits — a
    // search view has no single upload target (spec-web-ui §2/W3).
    const showUpload = this.canUpload;
    // Per-artifact action buttons come straight from capability data — the
    // component renders EXACTLY what the transport advertises (unknown ids
    // skipped), never branching on a host identity.
    const actions = actionDescriptors(this.transport.caps.actions);
    return html`
      <div class="nav-row">
        <modulina-select
          .label=${"Klientas"}
          .options=${this.clients.map((c) => ({ value: c.id, label: c.name }))}
          .value=${this.clientId}
          @change=${(e: CustomEvent<{ value: string }>) => void this.onClientChange(e.detail.value)}
        ></modulina-select>
        <modulina-select
          .label=${"Projektas"}
          .options=${this.projects.map((p) => ({ value: p.id, label: p.name }))}
          .value=${this.projectId}
          .disabled=${this.clientId === ""}
          @change=${(e: CustomEvent<{ value: string }>) => void this.onProjectChange(e.detail.value)}
        ></modulina-select>
      </div>

      <modulina-search-bar
        .allowScopeToggle=${this.projectId !== ""}
        .searchAll=${this.searchAll}
        .placeholder=${this.projectId === ""
          ? "Paieška visuose projektuose…"
          : "Paieška pagal pavadinimą…"}
        @scope-change=${(e: CustomEvent<{ searchAll: boolean }>) =>
          (this.searchAll = e.detail.searchAll)}
        @search=${(e: CustomEvent<{ q: string }>) => void this.onSearch(e.detail.q)}
      ></modulina-search-bar>

      ${showUpload
        ? html`<modulina-upload-panel
            .sourceMode=${this.transport.getModelInfo ? "activeModel" : "file"}
            .returnTarget=${this.returnTarget?.name ?? null}
            .fileName=${this.pendingUpload?.name ?? null}
            .fileSize=${this.pendingUpload?.size ?? null}
            .pendingNote=${this.uploadPendingNote}
            .appendInfo=${this.uploadAppendInfo}
            .uploading=${this.uploading}
            .progress=${this.uploadProgress}
            @file-chosen=${(e: CustomEvent<{ file: File }>) =>
              void this.onUploadFileChosen(e.detail.file)}
            @model-pick=${() => void this.onModelPick()}
            @upload-submit=${(e: CustomEvent<{ message: string }>) =>
              void this.onUploadSubmit(e.detail.message)}
            @upload-cancel=${this.onUploadCancel}
            @upload-abort=${this.onUploadAbort}
          ></modulina-upload-panel>`
        : nothing}

      ${this.lockPanel
        ? html`<modulina-lock-panel
            .mode=${this.lockPanel.mode}
            .fileName=${this.lockPanel.fileName}
            .confirmText=${this.lockPanel.confirmText}
            .busy=${this.lockBusy}
            @lock-confirm=${(e: CustomEvent<{ note: string }>) =>
              void this.onLockConfirm(e.detail.note)}
            @lock-cancel=${this.onLockCancel}
          ></modulina-lock-panel>`
        : nothing}

      <modulina-status
        .kind=${this.status.kind}
        .code=${this.status.code ?? ""}
        .text=${this.status.text ?? ""}
      ></modulina-status>

      ${this.actionRun ? this.renderActionRun(this.actionRun) : nothing}

      ${this.rowsLoaded
        ? html`<modulina-artifact-table
            .rows=${this.rows}
            .mode=${this.rowsMode}
            .selectedId=${this.openArtifact?.id ?? this.highlightId}
            .actions=${actions}
            .me=${this.me}
            .lockUi=${this.lockUi}
            .canReturn=${showUpload}
            .emptyText=${"Artefaktų nerasta."}
            @open-artifact=${(e: CustomEvent<{ row: ArtifactListRow }>) =>
              void this.openRow(e.detail.row)}
            @artifact-action=${(e: CustomEvent<ArtifactActionIntent>) =>
              this.onArtifactAction(e.detail)}
            @lock-action=${(e: CustomEvent<LockActionEvent>) => this.onLockAction(e.detail)}
          ></modulina-artifact-table>`
        : html`<p class="hint">
            Pasirinkite klientą ir projektą arba įveskite paiešką visuose projektuose.
          </p>`}

      ${this.openArtifact || this.panelLoading
        ? html`<modulina-versions-panel
            .artifact=${this.openArtifact}
            .loading=${this.panelLoading}
            .actions=${actions}
            @close=${this.closePanel}
            @artifact-action=${(e: CustomEvent<ArtifactActionIntent>) =>
              this.onArtifactAction(e.detail)}
          ></modulina-versions-panel>`
        : nothing}
    `;
  }

  /** Progress + cancel strip for an in-flight open/import (the adapter
   * streams progress and honors the abort signal — BRIDGE.md §5). The
   * browser never sets actionRun, so this never renders there. */
  private renderActionRun(run: { label: string }) {
    const p = this.actionProgress;
    const detail =
      p === null
        ? ""
        : p.totalBytes !== null && p.totalBytes > 0
          ? ` (${Math.min(100, Math.floor((p.bytes / p.totalBytes) * 100))}%)`
          : ` (${Math.round(p.bytes / 1024)} KB)`;
    return html`<div class="action-run">
      <span aria-live="polite">${run.label}…${detail}</span>
      <span class="spacer"></span>
      <button @click=${this.onActionAbort}>Atšaukti</button>
    </div>`;
  }

  // -------------------------------------------------------------------------
  // Data flows (every await funnels errors through fail())
  // -------------------------------------------------------------------------

  private async loadClients(): Promise<void> {
    if (!this.transport) return;
    try {
      this.clients = await this.transport.listClients();
      if (this.clients.length === 0) {
        this.status = { kind: "info", text: "Klientų dar nėra." };
      }
      await this.applyInitialContext();
    } catch (err) {
      this.fail(err);
    }
  }

  /** ONE-SHOT preselect from the shell (SketchUp assignment context): walk
   * client → project exactly like user picks would, but only for ids that
   * actually exist in the fetched lists — a stale assignment degrades
   * silently to the plain start view. Consumed on first application. */
  private async applyInitialContext(): Promise<void> {
    if (this.initialContextApplied) return;
    this.initialContextApplied = true;
    const clientId = this.initialClientId;
    if (!clientId || !this.clients.some((c) => c.id === clientId)) return;
    await this.onClientChange(clientId);
    const projectId = this.initialProjectId;
    if (!projectId || !this.projects.some((p) => p.id === projectId)) return;
    await this.onProjectChange(projectId);
  }

  private async onClientChange(clientId: string): Promise<void> {
    this.clientId = clientId;
    this.projectId = "";
    this.projects = [];
    this.resetRows();
    if (!clientId || !this.transport) return;
    try {
      this.projects = await this.transport.listProjects(clientId);
    } catch (err) {
      this.fail(err);
    }
  }

  private async onProjectChange(projectId: string): Promise<void> {
    this.projectId = projectId;
    this.resetRows();
    if (!projectId) return;
    await this.loadProjectRows("");
  }

  private async onSearch(q: string): Promise<void> {
    const tenantWide = this.searchAll || this.projectId === "";
    if (tenantWide) {
      if (q === "") {
        this.status = { kind: "info", text: "Įveskite paieškos tekstą." };
        return;
      }
      await this.loadSearchRows(q);
    } else {
      await this.loadProjectRows(q);
    }
  }

  private async loadProjectRows(q: string): Promise<void> {
    if (!this.transport || this.projectId === "") return;
    this.status = { kind: "loading" };
    try {
      const rows = await this.transport.listArtifacts({
        projectId: this.projectId,
        q: q || undefined,
      });
      this.rows = rows;
      this.rowsMode = "project";
      this.rowsLoaded = true;
      this.lastQuery = q;
      this.status = { kind: "" };
    } catch (err) {
      this.fail(err);
    }
  }

  private async loadSearchRows(q: string): Promise<void> {
    if (!this.transport) return;
    this.status = { kind: "loading" };
    try {
      const rows = await this.transport.searchArtifacts(q);
      this.rows = rows;
      this.rowsMode = "search";
      this.rowsLoaded = true;
      this.lastQuery = q;
      this.status = { kind: "" };
    } catch (err) {
      this.fail(err);
    }
  }

  /** Re-fetch the CURRENT view (spec-artifact-locking §8: after a take /
   * return / abandon / takeover the badges and the version count must be
   * right without a manual refresh). Same query, same mode — a lock change
   * must never silently change WHICH rows the user is looking at. */
  private async refreshRows(): Promise<void> {
    if (this.rowsMode === "search") {
      if (this.lastQuery) await this.loadSearchRows(this.lastQuery);
      return;
    }
    await this.loadProjectRows(this.lastQuery);
  }

  private async openRow(row: ArtifactListRow): Promise<void> {
    // Capture BEFORE any await: the versions panel steals focus on open,
    // and closePanel gives it back to exactly this element.
    this.restoreFocusTo = deepActiveElement();
    await this.openById(row.id);
    if (this.openArtifact) {
      this.dispatchEvent(
        new CustomEvent("artifact-opened", {
          detail: { id: row.id },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private async openById(id: string): Promise<void> {
    if (!this.transport) return;
    this.panelLoading = true;
    try {
      this.openArtifact = await this.transport.getArtifact(id);
    } catch (err) {
      this.openArtifact = null;
      this.fail(err);
    } finally {
      this.panelLoading = false;
    }
  }

  private closePanel = (): void => {
    this.openArtifact = null;
    this.dispatchEvent(new CustomEvent("artifact-closed", { bubbles: true, composed: true }));
    // Focus back where the user was (lit reuses row elements across
    // re-renders, so the captured node normally survives; if it was
    // removed, focus() on a detached element is a harmless no-op).
    this.restoreFocusTo?.focus();
    this.restoreFocusTo = null;
  };

  /**
   * Fulfil one action intent, EXHAUSTIVELY (PR #45 review minor 3): the
   * routing is pure capability data (planArtifactAction on the action id +
   * whether the transport can perform), never a host identity. "download"
   * is fulfilled here (mint presigned link → native download — the REST
   * shell's only action, byte-identical); open/import go to the shell's
   * performArtifactAction (SketchUp). An action a transport advertised but
   * cannot route is surfaced as a visible error — NEVER a silent no-op.
   */
  private onArtifactAction(intent: ArtifactActionIntent): void {
    const transport = this.transport;
    if (!transport) return;
    switch (planArtifactAction(intent.action, transport.performArtifactAction !== undefined)) {
      case "download":
        void this.download(
          intent.artifactId,
          intent.versionNumber,
          this.nameFor(intent.artifactId),
        );
        return;
      case "perform":
        void this.performAction(intent);
        return;
      case "unsupported":
        // The transport advertised this action but exposes no way to
        // fulfil it — a capability lie. Surface it plainly instead of
        // swallowing the click (the exhaustive-dispatch guarantee).
        this.status = {
          kind: "error",
          code: "not_implemented",
          text: `Veiksmas „${this.actionLabel(intent.action)}" šiame lange nepalaikomas.`,
        };
        return;
    }
  }

  /** Fulfil a non-download action through the shell's performArtifactAction
   * (open/import on SketchUp): loading → progress/cancel row → ok/aborted/
   * error. One action at a time; a click while busy is ignored. */
  private async performAction(intent: ArtifactActionIntent): Promise<void> {
    const transport = this.transport;
    const perform = transport?.performArtifactAction;
    if (!transport || !perform || this.actionRun) return;
    const label = this.actionLabel(intent.action);
    const name = this.nameFor(intent.artifactId);
    const controller = new AbortController();
    this.actionController = controller;
    this.actionRun = { label };
    this.actionProgress = null;
    this.status = { kind: "loading", text: `${label}…` };
    try {
      const result = await perform.call(transport, intent, {
        signal: controller.signal,
        onProgress: (bytes, totalBytes) => {
          this.actionProgress = { bytes, totalBytes };
        },
      });
      this.status = {
        kind: "ok",
        text: result.message ?? `${label}: ${result.filename ?? name}`,
      };
    } catch (err) {
      if (err instanceof TransportError && err.code === "action_aborted") {
        // An outcome the user chose (Atšaukti), not a failure.
        this.status = { kind: "info", text: "Veiksmas atšauktas." };
      } else {
        this.fail(err, `Nepavyko įvykdyti veiksmo „${name}".`);
      }
    } finally {
      this.actionRun = null;
      this.actionProgress = null;
      this.actionController = null;
    }
  }

  private onActionAbort = (): void => {
    this.actionController?.abort();
  };

  /** The user-facing label for an action id (capability data → descriptor
   * label; falls back to the raw id for a never-rendered unknown). */
  private actionLabel(action: ArtifactActionId): string {
    return actionDescriptors([action])[0]?.label ?? action;
  }

  /** Display name for an artifact from current state (open panel first,
   * then the visible rows) — used only for the download status/error text. */
  private nameFor(artifactId: string): string {
    if (this.openArtifact?.id === artifactId) return this.openArtifact.name;
    return this.rows.find((r) => r.id === artifactId)?.name ?? "";
  }

  /** Mint a presigned link and hand it to the host. Answers whether the
   * download actually started, so the take-for-work flow can report
   * „Paimta darbui" only when the bytes are really on their way (a failed
   * download after a successful lock must not read as a clean take). */
  private async download(
    artifactId: string,
    versionNumber: number | undefined,
    name: string,
  ): Promise<boolean> {
    if (!this.transport) return false;
    this.status = { kind: "loading", text: "Ruošiama atsisiuntimo nuoroda…" };
    try {
      const link = await this.transport.getDownloadLink(artifactId, versionNumber);
      this.transport.openExternal(link.url);
      this.status = { kind: "ok", text: `Atsisiuntimas pradėtas: ${link.filename}` };
      return true;
    } catch (err) {
      this.fail(err, `Nepavyko atsisiųsti „${name}".`);
      return false;
    }
  }

  // -------------------------------------------------------------------------
  // Take for work (spec-artifact-locking §8) — the user's vocabulary is
  // take/return; locks and version numbers never reach the screen.
  // -------------------------------------------------------------------------

  /** Route one row intent. Take and takeover open a confirm first (the note
   * / "whose work is this" step); abandon and return act immediately. */
  private onLockAction(detail: LockActionEvent): void {
    const row = this.rowFor(detail.artifactId);
    if (!row || this.lockBusy) return;
    switch (detail.action) {
      case "take":
        this.lockPanel = {
          mode: "take",
          artifactId: row.id,
          fileName: row.name,
          confirmText: "",
        };
        return;
      case "takeover":
        this.lockPanel = {
          mode: "takeover",
          artifactId: row.id,
          fileName: row.name,
          // Composed HERE (the clock lives in the component, the wording in
          // the pure lib) so the panel stays props-in.
          confirmText: takeoverConfirmText(row.name, row, Date.now()),
        };
        return;
      case "abandon":
        void this.abandon(row.id, row.name);
        return;
      case "return":
        this.beginReturn(row.id, row.name);
        return;
    }
  }

  /**
   * „Grąžinti" = upload the edited file back — through whichever SOURCE this
   * shell uploads from. The branch is the SAME capability predicate the
   * upload panel is rendered with (`getModelInfo` present = "activeModel"),
   * never a host identity:
   *
   * - "activeModel" (SketchUp): there IS no file input to open — the source
   *   is the active model, so the return drives the panel's own pick flow.
   *   Calling openPicker() here was a silent no-op and „Grąžinti" did
   *   nothing at all (channel bug report 127d).
   * - "file" (browser): SYNCHRONOUS on purpose — this runs inside the row
   *   button's click handler and opens the native picker directly, because
   *   browsers only honor a programmatic `input.click()` while the user's
   *   gesture is still in effect. Anything awaited before it would lose that
   *   activation and the picker would not open.
   *
   * Arming ALWAYS invalidates an open confirm first (the §8 confirm/return
   * invariant, {@link invalidatePendingUpload}): a confirm on screen was
   * built for a different upload, and letting it survive would let its
   * submit send those bytes to THIS artifact.
   */
  private beginReturn(artifactId: string, name: string): void {
    this.invalidatePendingUpload();
    this.returnTarget = { artifactId, name };
    this.status = { kind: "" };
    if (this.transport?.getModelInfo) {
      // No user-activation rule applies to an intent call — awaiting is fine.
      void this.onModelPick();
      return;
    }
    // `renderRoot` exists only once the element is connected to a document;
    // the cast keeps the STATE effect (returnTarget) drivable in the DOM-free
    // component harness, where the picker simply has nothing to open.
    const root = this.renderRoot as ParentNode | null;
    root?.querySelector<ModulinaUploadPanel>("modulina-upload-panel")?.openPicker();
  }

  /** Confirm of the open take/takeover panel. A takeover is the deliberate
   * TWO-STEP the spec names (§8 / mcp-spec unlock description): release the
   * colleague's lock, then claim the file in the user's own name — a
   * release without a re-claim would leave the file free for anyone. */
  private async onLockConfirm(note: string): Promise<void> {
    const panel = this.lockPanel;
    const transport = this.transport;
    if (!panel || !transport?.lockArtifact || !transport.unlockArtifact || this.lockBusy) return;
    const { artifactId, fileName, mode } = panel;
    const rowBase = this.rowFor(artifactId)?.currentVersionNumber ?? null;
    this.lockBusy = true;
    this.status = { kind: "loading", text: `${mode === "takeover" ? "Perimama" : "Paimama"}…` };
    try {
      let previousHolder: string | null = null;
      if (mode === "takeover") {
        const released = await transport.unlockArtifact(artifactId);
        previousHolder = released.previousLockedBy;
      }
      const claimed = await transport.lockArtifact(artifactId, note || undefined);
      this.lockPanel = null;
      // REMEMBER THE BASE before anything can fail: this is the whole point
      // of taking for work — the later return declares it and the server
      // refuses if the file moved on meanwhile. The CLAIM's answer wins over
      // the visible row (spec-artifact-locking §7 currentVersionNumber): the
      // row may have been rendered minutes ago, while the claim reports the
      // version the lock was actually taken against. A pre-1.3.0 server that
      // omits the field falls back to the row.
      const takenBase = claimed.currentVersionNumber ?? rowBase;
      if (takenBase !== null) this.baseMemory.remember(artifactId, takenBase);
      // Hand the bytes over only where this shell CAN (capability data, not
      // a host branch): the SketchUp dialog advertises open/import and has
      // no browser to navigate (ui/BRIDGE.md §4.3), so there a take is the
      // claim alone and the user loads the file with the row's own buttons.
      const downloaded = this.canDownload
        ? await this.download(artifactId, undefined, fileName)
        : null;
      await this.refreshRows();
      const from = previousHolder ? ` iš ${previousHolder}` : "";
      const took = mode === "takeover" ? "Perėmėte" : "Paėmėte";
      this.status =
        downloaded === false
          ? // The lock IS taken (the file is reserved) but the bytes never
            // left — say both, so the user knows to download manually rather
            // than assume the take failed and repeat it.
            {
              kind: "error",
              text:
                `Failas „${fileName}" paimtas darbui${from}, bet atsisiuntimas nepavyko — ` +
                `atsisiųskite jį atskirai arba spauskite „Atsisakyti".`,
            }
          : {
              kind: "ok",
              text:
                `${took} darbui „${fileName}"${from} — ` +
                `${downloaded ? "atsisiuntimas pradėtas" : this.loadHint()}. ` +
                `Baigę spauskite „Grąžinti".`,
            };
    } catch (err) {
      this.fail(err, `Nepavyko paimti „${fileName}".`);
    } finally {
      this.lockBusy = false;
    }
  }

  private onLockCancel = (): void => {
    this.lockPanel = null;
  };

  /** „Atsisakyti" — release without uploading (the edit was abandoned). The
   * remembered base goes with it: keeping it would declare a stale base on
   * the user's NEXT plain upload of the same file. */
  private async abandon(artifactId: string, name: string): Promise<void> {
    const transport = this.transport;
    if (!transport?.unlockArtifact || this.lockBusy) return;
    this.lockBusy = true;
    this.status = { kind: "loading", text: `Atsisakoma „${name}"…` };
    try {
      await transport.unlockArtifact(artifactId);
      this.baseMemory.forget(artifactId);
      if (this.returnTarget?.artifactId === artifactId) {
        this.returnTarget = null;
        // The confirm on screen (if any) was the return of the file just
        // abandoned — it no longer describes anything sendable, and leaving
        // it would let submit fall back to a NAME-resolved upload of those
        // bytes (§8 confirm/return invariant).
        this.invalidatePendingUpload();
      }
      await this.refreshRows();
      this.status = { kind: "ok", text: `Atsisakėte „${name}" — failas vėl laisvas.` };
    } catch (err) {
      this.fail(err, `Nepavyko atsisakyti „${name}".`);
    } finally {
      this.lockBusy = false;
    }
  }

  /** The visible row for an id (lock decisions read the row the user is
   * actually looking at — the same data the badge was rendered from). */
  private rowFor(artifactId: string): ArtifactListRow | ArtifactSearchRow | undefined {
    return this.rows.find((r) => r.id === artifactId);
  }

  // -------------------------------------------------------------------------
  // Upload flow (W3): the component orchestrates UX; ALL upload semantics
  // (create-vs-append, author, multipart) live in the adapter's uploadFile.
  // -------------------------------------------------------------------------

  /** "file" source: the picked browser File becomes the pending
   * descriptor, carrying the File itself for the REST adapter's byte path.
   *
   * RETURN GUARD (spec-artifact-locking §8): when the pick was started by
   * „Grąžinti", the file must be the one being returned. A differently
   * named file would not append — it would quietly CREATE a second artifact
   * and leave the taken file still checked out, which is precisely the
   * silent-loss class this feature exists to prevent. */
  private async onUploadFileChosen(file: File): Promise<void> {
    const target = this.returnTarget;
    if (target && file.name !== target.name) {
      this.status = {
        kind: "error",
        text:
          `Pasirinktas failas „${file.name}" neatitinka grąžinamo failo „${target.name}". ` +
          "Pasirinkite tą patį failą arba atšaukite grąžinimą.",
      };
      return;
    }
    await this.beginPendingUpload({ name: file.name, size: file.size, file }, null);
  }

  /** "activeModel" source (spec-upload-unification §3): ask the transport
   * for the active model's facts. A never-saved model (fileName null) is
   * refused up front with `model_not_saved` — the UI pre-check half of the
   * double protection (the host's upload.perform rejects it too); a dirty
   * model proceeds WITH the pending note (the host saves before
   * uploading). The snapshot only seeds the shared pending/confirm flow —
   * upload.perform re-reads the active model itself. */
  private async onModelPick(): Promise<void> {
    const transport = this.transport;
    if (!transport?.getModelInfo || this.projectId === "" || this.modelPickInFlight) {
      return;
    }
    this.modelPickInFlight = true;
    try {
      const info = await transport.getModelInfo();
      if (info.fileName === null) {
        this.status = { kind: "error", code: "model_not_saved" };
        return;
      }
      await this.beginPendingUpload(
        { name: info.fileName, size: info.sizeBytes },
        info.dirty ? DIRTY_MODEL_NOTE : null,
      );
    } catch (err) {
      this.fail(err);
    } finally {
      this.modelPickInFlight = false;
    }
  }

  /** Shared pending entry for BOTH sources: fetch a FRESH unfiltered list
   * for the pre-send confirm — the visible rows may be q-filtered, and a
   * missed "this will append" warning is exactly the surprise the spec
   * forbids. The adapter re-checks authoritatively at send time either
   * way. */
  private async beginPendingUpload(
    pending: PendingUpload,
    pendingNote: string | null,
  ): Promise<void> {
    if (!this.transport || this.projectId === "") return;
    // SNAPSHOT the return target into the pick itself: from here on THIS
    // upload is (or is not) a return, whatever the live field does later.
    const target = this.returnTarget;
    this.pendingUpload = target ? { ...pending, returnTo: target } : pending;
    this.uploadPendingNote = pendingNote;
    this.uploadAppendInfo = null;
    this.status = { kind: "" };
    try {
      const rows = await this.transport.listArtifacts({ projectId: this.projectId });
      if (target) {
        // A RETURN has an authoritative target: the file whose lock the user
        // holds. Its ID decides — NOT the name (an artifact may be named
        // `dd` while the model on disk is `dd.skp`, and two artifacts may
        // share one name), because a name match here would append the user's
        // work to a different, unlocked file (channel bug report 127d).
        const row = rows.find((r) => r.id === target.artifactId);
        if (!row) {
          // The file is gone (deleted, or another tenant context) — say so
          // instead of falling back to a name match that could create a new
          // artifact out of a return. DISARM as well: this return is
          // definitively impossible, and a target row that no longer exists
          // has no „Atsisakyti" button left to clear it with — a still-armed
          // target would strand the panel on „Grąžinti…" and block every
          // plain upload until the user switches project.
          this.invalidatePendingUpload();
          this.returnTarget = null;
          this.status = {
            kind: "error",
            code: "artifact_not_found",
            text: `Grąžinamo failo „${target.name}" nebėra šiame projekte.`,
          };
          return;
        }
        this.uploadAppendInfo = {
          artifactId: row.id,
          artifactName: row.name,
          // Same display estimate the planner's append branch computes.
          nextVersionNumber: (row.currentVersionNumber ?? row.versionCount) + 1,
          currentVersionNumber: row.currentVersionNumber ?? 0,
          lockState: lockStateOf(row, this.me),
        };
        return;
      }
      // Author placeholder: the plan here only drives the confirm UI; the
      // adapter rebuilds it with the real session author on submit.
      const plan = planUpload(rows, pending.name, "-");
      if (plan.kind === "ambiguous") {
        this.invalidatePendingUpload();
        this.status = { kind: "error", code: "artifact_name_ambiguous" };
        return;
      }
      if (plan.kind === "append") {
        // The lock/base facts come from the SAME fresh row the plan matched,
        // so what the confirm shows and what submit decides cannot disagree.
        const matched = rows.find((r) => r.id === plan.artifactId);
        this.uploadAppendInfo = {
          artifactId: plan.artifactId,
          artifactName: plan.artifactName,
          nextVersionNumber: plan.nextVersionNumber,
          currentVersionNumber: plan.currentVersionNumber,
          lockState: matched ? lockStateOf(matched, this.me) : "free",
        };
      }
    } catch (err) {
      this.invalidatePendingUpload();
      this.fail(err);
    }
  }

  /**
   * Drop the open pick and everything the confirm was built from (THE §8
   * confirm/return invariant): a confirm describes exactly ONE upload, so the
   * moment the return target changes — armed by „Grąžinti", cleared by
   * „Atsisakyti" or by a target that vanished — what is on screen no longer
   * describes what submit would send, and the stale confirm must not be
   * submittable at all. Clears only the PICK; `returnTarget` itself belongs
   * to the caller (arming vs disarming differ).
   */
  private invalidatePendingUpload(): void {
    this.pendingUpload = null;
    this.uploadPendingNote = null;
    this.uploadAppendInfo = null;
  }

  private async onUploadSubmit(message: string): Promise<void> {
    const pending = this.pendingUpload;
    const append = this.uploadAppendInfo;
    if (!this.transport || !pending || this.projectId === "" || this.uploading) return;
    this.uploading = true;
    this.uploadProgress = null;
    const controller = new AbortController();
    this.uploadController = controller;
    try {
      // SILENT AUTO-LOCK (spec-artifact-locking §8, owner verdict variant 1):
      // every upload of an EXISTING file goes through the take-for-work path,
      // so a file the user never took is claimed first — no warning, no extra
      // click, no wild uploads at shell level. A file a colleague holds is
      // refused HERE by the server (`lock_held`, naming the holder) instead of
      // overwriting their work; a file the user already holds skips the call.
      await this.autoLockBeforeUpload(append);
      const result = await this.transport.uploadFile({
        projectId: this.projectId,
        // Present on the "file" source only — the SketchUp intent path
        // carries no bytes (UploadFileArgs.file is optional by design).
        file: pending.file,
        // A RETURN names its target so no adapter (and no SketchUp host)
        // resolves it by file name — the id of the file the user holds is
        // the only authority (spec-artifact-locking §8; bug report 127d).
        // Absent on an ordinary upload: create-vs-append stays name-based.
        // Read off THIS pick's snapshot, never the live `returnTarget`: the
        // field can have been re-armed or cleared since the confirm was
        // built, and sending one pick's bytes to another pick's artifact is
        // the same wrong-target class one layer up (PendingUpload.returnTo).
        artifactId: pending.returnTo?.artifactId,
        message: message.trim() || undefined,
        // The version this edit started from (invisible to the user): the
        // remembered take, or the file's current version for an upload that
        // never went through „Paimti darbui".
        baseVersionNumber: append
          ? baseVersionToDeclare(
              this.baseMemory.baseFor(append.artifactId),
              append.currentVersionNumber,
            )
          : undefined,
        // The kind THIS confirm was built on. The adapter re-decides
        // create-vs-append authoritatively at send time; if a colleague
        // created (or removed) a same-named file in between, its answer
        // disagrees with this one and the upload is refused instead of
        // taking the other path — an append planned as a create would
        // carry neither the auto-lock above nor a base (§8 discipline).
        confirmedKind: append ? "append" : "create",
        onProgress: (sentBytes, totalBytes) => {
          this.uploadProgress = { sentBytes, totalBytes };
        },
        signal: controller.signal,
      });
      this.pendingUpload = null;
      this.uploadPendingNote = null;
      this.uploadAppendInfo = null;
      // The check-in released the lock server-side (spec §4.3 auto-release)
      // and consumed the base — forget it, or the user's NEXT plain upload
      // of this file would declare a version that is no longer current.
      this.baseMemory.forget(append?.artifactId ?? result.artifactId);
      this.returnTarget = null;
      // Refresh WITHOUT the q filter so the new/updated row is guaranteed
      // visible, then highlight it and report the version honestly. The
      // author comes from the API RESPONSE (result.author), never a local
      // guess — B-39: the server derives it from the session user; the
      // refreshed table's Autorius column is API-fed the same way.
      await this.loadProjectRows("");
      this.highlightId = result.artifactId;
      this.status = {
        kind: "ok",
        text: `Įkelta „${result.artifactName}" — versija v${result.versionNumber} (autorius: ${result.author}).`,
      };
    } catch (err) {
      if (err instanceof TransportError && err.code === "upload_aborted") {
        // An outcome the user chose, not an error; the pick stays pending
        // so a retry is one click away.
        this.status = { kind: "info", text: "Įkėlimas atšauktas." };
      } else {
        this.fail(err, `Nepavyko įkelti „${pending.name}".`);
      }
    } finally {
      this.uploading = false;
      this.uploadProgress = null;
      this.uploadController = null;
    }
  }

  /**
   * The silent auto-lock step (§8). Deliberately does NOT decide who may
   * upload — `planUploadLock` says whether to CLAIM, and the SERVER answers:
   * `lockArtifact` never steals a colleague's lock, so a foreign lock comes
   * back as `lock_held` with the holder's name, which the status strip turns
   * into a sentence naming them. A transport without locking (an older
   * shell) uploads exactly as it did before — the guard is shell-side
   * discipline, and the server keeps allowing lock-less uploads for machine
   * seats (spec §2).
   *
   * BLOCKING vs BEST-EFFORT (supervisor ruling on channel reply 127 item 5,
   * documented in ui/BRIDGE.md §4.4): a claim the SERVER refused (`lock_held`
   * and every other answered status) CANCELS the upload — failing fast is
   * strictly better than sending bytes the server's own `artifact_locked`
   * guard would reject, and the refusal names the holder. A claim that never
   * got an answer (network down, bridge gone — `status === null`) does NOT
   * cancel it: the auto-lock is invisible infrastructure, not a new failure
   * mode in front of an upload that used to work, and the server guard is
   * the real gate. Such a skip is logged LOUDLY — an unguarded return upload
   * must never be silent.
   */
  private async autoLockBeforeUpload(append: PendingAppend | null): Promise<void> {
    const lockArtifact = this.transport?.lockArtifact;
    if (!append || !lockArtifact || !this.transport) return;
    if (planUploadLock("append", append.lockState) === "skip") return;
    try {
      await lockArtifact.call(this.transport, append.artifactId);
    } catch (err) {
      if (err instanceof TransportError && err.status === null) {
        console.error(
          `modulina: auto-lock before upload did not reach the server (${err.code}) — ` +
            `uploading "${append.artifactName}" WITHOUT a fresh claim; ` +
            `the server's artifact_locked guard remains the gate`,
          err,
        );
        return;
      }
      throw err;
    }
  }

  private onUploadCancel = (): void => {
    this.invalidatePendingUpload();
    // Cancelling the pick also cancels the RETURN it may have started; the
    // file stays taken (the user can retry or „Atsisakyti").
    this.returnTarget = null;
  };

  private onUploadAbort = (): void => {
    this.uploadController?.abort();
  };

  private resetRows(): void {
    // Navigating away (client/project change) mid-upload aborts it — the
    // panel is about to disappear, a silently continuing upload into the
    // previous project would be a surprise. Same for an in-flight
    // open/import fulfilment.
    this.uploadController?.abort();
    this.actionController?.abort();
    this.actionRun = null;
    this.actionProgress = null;
    this.rows = [];
    this.rowsLoaded = false;
    this.searchAll = false;
    this.openArtifact = null;
    this.status = { kind: "" };
    this.pendingUpload = null;
    this.uploadPendingNote = null;
    this.uploadAppendInfo = null;
    this.highlightId = null;
    this.lockPanel = null;
    this.returnTarget = null;
  }

  /** One error funnel: 401 → the shell's problem (login view); anything
   * else → the status strip with the registry code + LT gloss.
   *
   * The locking refusals get the RICHEST text they can: `lock_held` /
   * `artifact_locked` / `version_conflict` carry the holder or the winning
   * version in their details, and a sentence naming who and when beats both
   * the generic gloss and the caller's context line (spec §8 "Conflict
   * message"). The registry code still rides along in the strip. */
  private fail(err: unknown, contextText?: string): void {
    if (err instanceof TransportError && err.sessionExpired) {
      this.dispatchEvent(
        new CustomEvent("session-expired", { bubbles: true, composed: true }),
      );
      return;
    }
    const code = err instanceof TransportError ? err.code : "internal_error";
    const details = err instanceof TransportError ? err.details : undefined;
    const lockText = lockErrorText(code, details, Date.now());
    this.status = { kind: "error", code, text: lockText ?? contextText ?? "" };
  }
}

/** The REAL focused element across shadow boundaries: document.activeElement
 * only reports the outermost host — walk activeElement chains down into
 * shadow roots to reach the actual row/button that has focus. */
function deepActiveElement(): HTMLElement | null {
  let el: Element | null = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el instanceof HTMLElement ? el : null;
}

customElements.define("modulina-artifact-browser", ModulinaArtifactBrowser);
